import { useEffect, useState } from 'react';
import { listPurchaseOrderLines, listPurchaseRequests } from '../api/purchasing';
import { listItems, listSuppliers, listUoms } from '../api/masterData';
import { useBranch } from '../context/BranchContext';
import { Drawer, DrawerField, EmptyState, ErrorNote, LoadingNote, StatusBadge, tableStyle, tdStyle, thStyle } from './common';
import type { Item, PurchaseOrder, PurchaseOrderLine, PurchaseRequest, Supplier, Uom } from '../types/domain';

/**
 * Read-only Purchase Order detail drawer — reused by every screen that
 * lists PO rows (Purchase Orders page, Purchasing dashboard) so "No. PO"
 * is a real, inspectable document everywhere it appears. Self-fetches
 * lines/suppliers/PRs/items/UOMs when opened so call sites don't each
 * need to preload master data just to show a drawer.
 */
export function PoDetailDrawer({ po, open, onClose }: { po: PurchaseOrder | null; open: boolean; onClose: () => void }) {
  const { branches } = useBranch();
  const [lines, setLines] = useState<PurchaseOrderLine[] | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [prs, setPrs] = useState<PurchaseRequest[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!po || !open) return;
    setLines(null);
    setError(null);
    Promise.all([listPurchaseOrderLines(po.po_id), listSuppliers(), listPurchaseRequests(), listItems(), listUoms()])
      .then(([lineRows, supplierRows, prRows, itemRows, uomRows]) => {
        setLines(lineRows);
        setSuppliers(supplierRows);
        setPrs(prRows);
        setItems(itemRows);
        setUoms(uomRows);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [po, open]);

  const itemLabel = (id: string) => {
    const it = items.find((i) => i.item_id === id);
    return it ? `${it.item_code} — ${it.name}` : id;
  };
  const uomLabel = (id: string) => uoms.find((u) => u.uom_id === id)?.code ?? id;
  const supplierName = (id: string) => suppliers.find((s) => s.supplier_id === id)?.name ?? id;
  const prNumber = (id: string | null) => (id ? (prs.find((pr) => pr.pr_id === id)?.pr_number ?? id) : '—');
  const branchName = (id: string) => branches.find((b) => b.branch_id === id)?.name ?? id;

  return (
    <Drawer open={open && !!po} onClose={onClose} title={po ? `PO ${po.po_number}` : 'Detail PO'}>
      {po && (
        <div>
          <DrawerField label="No. PO" value={po.po_number} />
          <DrawerField label="Status" value={<StatusBadge status={po.status} />} />
          <DrawerField label="Supplier" value={supplierName(po.supplier_id)} />
          <DrawerField label="Cabang" value={branchName(po.branch_id)} />
          <DrawerField label="Sumber PR" value={prNumber(po.pr_id)} />
          <DrawerField label="Diterbitkan" value={po.issued_at ? new Date(po.issued_at).toLocaleString('id-ID') : '—'} />
          <DrawerField label="Dibuat" value={new Date(po.created_at).toLocaleString('id-ID')} />

          <div style={{ marginTop: 16, fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-secondary)', marginBottom: 6 }}>Baris PO</div>
          {error && <ErrorNote text={error} />}
          {!error && lines === null && <LoadingNote text="Memuat baris PO..." />}
          {lines && lines.length === 0 && <EmptyState text="Tidak ada baris pada PO ini." />}
          {lines && lines.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Item</th>
                  <th style={thStyle}>Qty dipesan</th>
                  <th style={thStyle}>Qty diterima</th>
                  <th style={thStyle}>UOM</th>
                  <th style={thStyle}>Harga satuan</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.po_line_id}>
                    <td style={tdStyle}>{itemLabel(l.item_id)}</td>
                    <td style={tdStyle}>{l.qty_ordered}</td>
                    <td style={tdStyle}>{l.qty_received}</td>
                    <td style={tdStyle}>{uomLabel(l.uom_id)}</td>
                    <td style={tdStyle}>{l.unit_price.toLocaleString('id-ID')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Drawer>
  );
}
